import { formatDateToDDMMYYYY } from '~/utils/campaignUtils';
import { format, differenceInCalendarDays } from 'date-fns';
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import DOMPurify from 'dompurify'

export function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export const NOTIFICATION_PERIODS = ['Notify Now', '1 Week', '2 Weeks', '3 Weeks', '1 Month', 'Do not Notify']

export const NOTIFICATION_INTERVALS = {
  'Notify Now': 0,
  '1 Week': 1,
  '2 Weeks': 2,
  '3 Weeks': 3,
  '1 Month': 4,
  'Do not Notify': 5,
}

export const toSelectValue = label => (label ? { label, value: label } : null)

export const fetchNotificationOptions = () => ({
  options: NOTIFICATION_PERIODS.map(period => ({ label: period, value: period })),
  pagination: { currentPage: 1, totalPages: 1 },
})

const SHORT_DATE_FORMAT_OPTIONS = { month: 'short', day: 'numeric' };
const formatShortDate = (date) => date.toLocaleDateString('en-US', SHORT_DATE_FORMAT_OPTIONS);

// Helper function to get Date objects from time filter title
export const getDateObjectsFromTimeFilter = (timeFilterTitle) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (timeFilterTitle) {
    case 'Today': {
      return { from: today, to: today };
    }
    case 'Last 7 days': {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      return { from: start, to: today };
    }
    case 'Last month': {
      const start = new Date(today);
      start.setMonth(today.getMonth() - 1);
      return { from: start, to: today };
    }
    case 'Last 3 months': {
      const start = new Date(today);
      start.setMonth(today.getMonth() - 3);
      return { from: start, to: today };
    }
    case 'Last year': {
      const targetYear = today.getFullYear() - 1;
      const month = today.getMonth();
      const day = today.getDate();
      const start = new Date(targetYear, month, 1);
      const lastDay = new Date(targetYear, month + 1, 0).getDate();
      start.setDate(Math.min(day, lastDay));
      return { from: start, to: today };
    }
    case 'Quarter to date': {
      const currentMonth = today.getMonth();
      const quarterStartMonth = Math.floor(currentMonth / 3) * 3;
      const start = new Date(today.getFullYear(), quarterStartMonth, 1);
      return { from: start, to: today };
    }
    case 'Month to date': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: start, to: today };
    }
    case 'Year to date': {
      const start = new Date(today.getFullYear(), 0, 1);
      return { from: start, to: today };
    }
    case 'All time':
    case 'Custom': {
      return undefined;
    }
    default:
      return { from: today, to: today };
  }
};

export const getStartAndEndDate = (filterObj) => {
  if (filterObj.customDate && filterObj.customDate.from && filterObj.customDate.to) {
    return {
      start: formatDateToDDMMYYYY(filterObj.customDate.from),
      end: formatDateToDDMMYYYY(filterObj.customDate.to)
    };
  }

  if (filterObj.dateRange && filterObj.timeFilter !== 'All time') {
    const currentDate = new Date();

    const isLastYear =
      filterObj.timeFilter &&
      (
        filterObj.timeFilter === 'Last year' ||
        (typeof filterObj.timeFilter === 'object' && filterObj.timeFilter.title === 'Last year')
      );

    if (isLastYear) {
      const startDate = new Date(currentDate);
      startDate.setFullYear(currentDate.getFullYear() - 1);
      return {
        start: formatDateToDDMMYYYY(startDate),
        end: formatDateToDDMMYYYY(currentDate)
      };
    }

    const currentYear = currentDate.getFullYear();
    const match = filterObj.dateRange.match(/^([A-Za-z]+) (\d{1,2}) - ([A-Za-z]+) (\d{1,2})$/);

    if (match) {
      const [, startMonth, startDay, endMonth, endDay] = match;
      const startMonthIndex = new Date(`${startMonth} 1, ${currentYear}`).getMonth();
      const endMonthIndex = new Date(`${endMonth} 1, ${currentYear}`).getMonth();
      const startYear = currentYear;
      let endYear = currentYear;

      if (endMonthIndex < startMonthIndex) {
        endYear = currentYear + 1;
      }

      const startDate = new Date(`${startMonth} ${startDay}, ${startYear}`);
      const endDate = new Date(`${endMonth} ${endDay}, ${endYear}`);

      return {
        start: formatDateToDDMMYYYY(startDate),
        end: formatDateToDDMMYYYY(endDate)
      };
    }

    return { start: null, end: null };
  }

  return {
    start: '01-01-2025',
    end: null
  };
};

// Generate date range based on time filter selection
export function getCurrentDateRange(timeFilterTitle) {
  if (timeFilterTitle === 'All time') {
    return 'All time';
  }

  if (timeFilterTitle === 'Custom') {
    return 'Custom';
  }

  const dateObjects = getDateObjectsFromTimeFilter(timeFilterTitle);

  if (!dateObjects) {
    const today = new Date();
    return `${formatShortDate(today)} - ${formatShortDate(today)}`;
  }

  return `${formatShortDate(dateObjects.from)} - ${formatShortDate(dateObjects.to)}`;
}

export const formatDateToPPPIfValid = (date) => {
  if (!date) return ''
  const parsedDate = parseDate(date)
  if (parsedDate && !isNaN(parsedDate.getTime())) {
    return format(parsedDate, 'PPP')
  }
  return ''
}

export const parseDate = (date) => {
  if (!date) return null;

  if (date instanceof Date) {
    return date;
  }

  if (typeof date === 'string') {
    // For date strings in 'YYYY-MM-DD' format, parse them as local dates to avoid timezone offset
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    } else {
      return new Date(date);
    }
  }

  return new Date(date);
}

export const formatDateToCompleteDate = (date) => {
  if (!date) return ''

  const dateObj = parseDate(date);
  if (!dateObj) return '';

  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // months are 0-indexed
  const year = dateObj.getFullYear();

  return `${day}/${month}/${year}`;
}

export const formatDate = (date) => {
  if (!date) return ''

  const dateObj = parseDate(date);
  if (!dateObj || isNaN(dateObj.getTime())) return '';

  return format(dateObj, 'MMMM dd, yyyy');
}

export function buildRansackQueryString(filters) {
  const params = new URLSearchParams()
  const { show_templates, ...rest } = filters
  Object.entries(rest).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      const isArray = Array.isArray(value)
      const paramKey = isArray ? `q[${key}][]` : `q[${key}]`

      if (isArray) {
        value.forEach(v => {
          if (v !== undefined && v !== null && v !== '') {
            params.append(paramKey, v.toString())
          }
        })
      } else {
        params.append(paramKey, value.toString())
      }
    }
  })

  if (show_templates) params.append('show_templates', true)

  return params.toString()
}

// Builds a full training catalog URL with filters
export function buildTrainingCatalogUrl(companyId, filters, trainingsCatalogPath) {
  const queryString = buildRansackQueryString(filters)
  const baseUrl = trainingsCatalogPath(companyId)
  return `${baseUrl}${queryString ? `?${queryString}` : ''}`
}

// flex-wrap, not CSS grid: a grid's auto-fill/auto-fit tracks get reserved or collapsed for
// the whole row, which either left-aligns a partial row with empty space or stretches cards
// edge-to-edge. Flex items just wrap at their own min/max width instead (see TrainingCard,
// TrainingCardTabs, RecommendedTrainings - ENG-12641).
export const DEFAULT_GRID_CLASSES = 'flex flex-wrap justify-start gap-4 sm:gap-6 lg:gap-8'

export const formatTimeAgo = (dateString) => {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

export const copyTextToClipboard = async ({
    text,
    successMessage = 'Copied to clipboard.',
    errorMessage = 'Failed to copy to clipboard.',
    toast,
}) => {
    try {
        await navigator.clipboard.writeText(String(text))
        toast?.success(successMessage, { showCloseButton: false })
        return true
    } catch {
        toast?.critical(errorMessage, { showCloseButton: false })
        return false
    }
}

export const scrollToTop = () => {
  if (typeof window === 'undefined') return
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

export const openUrl = (url, newTab = false, toast) => {
    if (!url) {
        toast?.critical('Link is not available.', { showCloseButton: false })
        return
    }
    if (newTab) window.open(url, '_blank', 'noopener,noreferrer')
    else window.location.assign(url)
}

export const formatLastActive = (dateString) => {
    if (!dateString) return '—'
    const days = differenceInCalendarDays(new Date(), new Date(dateString))
    return days <= 0 ? 'Today' : `${days}d ago`
}

export const normalizePagination = (raw = {}) => ({
    currentPage: raw.current_page ?? raw.currentPage ?? 1,
    totalPages: raw.total_pages ?? raw.totalPages ?? 1,
    totalCount: raw.total_count ?? raw.totalCount ?? 0,
    perPage: raw.per_page ?? raw.perPage ?? 50,
})

export const TABLE_STATUS_CONFIG = {
    draft: {
        label: 'Draft',
        bgColor: 'bg-llama-grey-9 text-llama-grey-10',
        dotColor: 'bg-llama-grey-10',
    },
    completed: {
        label: 'Completed',
        bgColor: 'bg-llama-green-1 text-llama-green-6',
        dotColor: 'bg-llama-green-6',
    },
    running: {
        label: 'Running',
        bgColor: 'bg-llama-green-1 text-llama-green-6',
        dotColor: 'bg-llama-green-6',
    },
    canceled: {
        label: 'Canceled',
        bgColor: 'bg-llama-grey-9 text-llama-grey-10',
        dotColor: 'bg-llama-grey-10',
    },
}

export const restoreBodyPointerEvents = (delayMs = 600) => {
  if (typeof document === 'undefined') return
  setTimeout(() => {
    if (document.body.style.pointerEvents === 'none') {
      document.body.style.pointerEvents = ''
    }
  }, delayMs)
}

export const isDateBeforeToday = (date, referenceDate = new Date()) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false
  const today = new Date(referenceDate)
  today.setHours(0, 0, 0, 0)
  return date < today
}

// dompurify needs a DOM. Under React on Rails server prerendering there is no window, so the
// default export is the uninitialised factory and .sanitize does not exist there - this is
// the only helper's sole guard against unsanitized HTML in that context, so a caller must
// explicitly vouch for its input (assumeServerSanitized) to get it verbatim; every other
// caller degrades to '' rather than silently skip the allowedTags/allowedAttr allowlist it
// asked for. Only pass assumeServerSanitized when the HTML was already run through a
// server-side allowlist for this exact use (e.g. AnnouncementBodyRenderer).
export const sanitizeHtml = (html, allowedTags = ['i'], allowedAttr = ['class'], { assumeServerSanitized = false } = {}) => {
  if (!html) return ''
  if (typeof DOMPurify.sanitize !== 'function') return assumeServerSanitized ? html : ''
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttr,
  })
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const validateEmailListField = (value) => {
  const emails = (value ?? '')
    .split(',')
    .map((address) => address.trim())
    .filter(Boolean)

  if (!emails.length) return 'Please enter at least one email, e.g. person1@gmail.com, person2@gmail.com, person3@gmail.com'

  const invalidEmail = emails.find((email) => !EMAIL_REGEX.test(email))
  if (invalidEmail) return `"${invalidEmail}" is not valid. Use the format: person1@gmail.com, person2@gmail.com, person3@gmail.com`

  return null
}

export const isContainsPredicate = (predicate) => ['_cont', '_not_cont', '_start', '_end'].includes(predicate)

// Automations::BuildRules::KEY_OPERATOR_OVERRIDES only allows in/contains for these backend rule keys
export const IS_AND_CONTAINS_ONLY_KEYS = ['group_id', 'with_group_id', 'group_names', 'missing_details_for_training']

// Automations::BuildRules::KEY_OPERATOR_OVERRIDES/VALID_OPERATORS allow starts_with/ends_with for these contains fields
const TEXT_MATCH_SUPPORTED_CONTAINS_FIELDS = ['department_name', 'location_name', 'region_name', 'role', 'employment_type', 'status', 'address_country']

export const scopeInclusionPredicatesForField = (predicates, filter) => {
  if (!predicates) return predicates

  const containsField = filter?.containsField
  const isKey = filter?.paramName || filter?.filterName

  if (!containsField) {
    const labels = { _in: predicates.labels?._in, _not_in: predicates.labels?._not_in }
    if (IS_AND_CONTAINS_ONLY_KEYS.includes(isKey) || !labels._not_in) delete labels._not_in
    return { ...predicates, labels }
  }

  const labels = { ...predicates.labels }
  if (IS_AND_CONTAINS_ONLY_KEYS.includes(isKey)) {
    delete labels._not_in
    delete labels._null
    delete labels._not_null
  }
  if (!TEXT_MATCH_SUPPORTED_CONTAINS_FIELDS.includes(containsField)) {
    delete labels._start
    delete labels._end
  }
  if (IS_AND_CONTAINS_ONLY_KEYS.includes(containsField)) delete labels._not_cont
  return { ...predicates, labels }
}

export const learnerGroupsPredicatesConfig = {
  inclusion: {
    IN: '_in',
    NOT_IN: '_not_in',
    CONTAINS: '_cont',
    NOT_CONTAINS: '_not_cont',
    labels: {
      _in: 'is',
      _not_in: 'is not',
      _cont: 'contains',
      _not_cont: 'does not contain',
    },
  },
}
